import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

// UI

import CustomerDetailModal from '@/features/customers/components/CustomerDetailModal'
import { Box, Flex } from '@chakra-ui/react'
import { MdDeleteOutline, MdOutlineCheckCircle } from 'react-icons/md'
import CustomerListHeader from '../components/CustomerListHeader'

//Hooks
import { useCustomer } from '@/features/customers/useCustomer'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//helper

import { useInitialTableState } from '@/components/Table/components/useInitialTableState'
import FullTable from '@/components/Table/FullTable/FullTable'
import { convertTableStateToQuery } from '@/utils/tableHelper'
import { createColumnHelper } from '@tanstack/react-table'

import FileViewCell from '@/components/Table/components/Cell/FileViewCell'
import { getCustomerTableColumns } from '../constants/customerTableColumns'
import { LoadingSection } from '@/components/Elements'

const CustomerListPage = () => {
  //State
  const [modalDetail, setModalDetail] = useState({
    show: false,
    isCreate: true,
  })

  const [storedTableState, setStoredTableState] = useInitialTableState(`table_state:customer`, {})
  const query = convertTableStateToQuery(storedTableState)

  // Hooks
  const { customerId } = useParams()

  const { useFetchAllCustomers, useRemoveCustomer, useUpdateCustomer } = useCustomer()
  const dispatch = useDispatch()
  const columnHelper = createColumnHelper()
  const removeCustomer = useRemoveCustomer()
  const updateCustomer = useUpdateCustomer()

  //Fetch
  const { data: customersResponse, isLoading } = useFetchAllCustomers(query)
  const { data: allCustomersResponse } = useFetchAllCustomers({ page: 1, pageSize: 0 }) // Page Size 0 means get all

  //Handle Open Customer modal by link
  useEffect(() => {
    if (customerId) {
      const customerDetail = allCustomersResponse?.data.find((customer) => customer._id === customerId) || {}
      setModalDetail({
        show: true,
        isCreate: false,
        detail: customerDetail,
      })
    }
  }, [customerId, allCustomersResponse])

  // Handler
  const handleAddCustomer = () => {
    setModalDetail({
      show: true,
      isCreate: true,
      detail: {},
    })
  }
  const handleOnRowClick = (row) => {
    setModalDetail({
      show: true,
      isCreate: false,
      detail: row?.original,
    })
  }

  const handleDelete = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await removeCustomer.mutateAsync(id)
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, removeCustomer]
  )
  const handleActivate = useCallback(
    async (id) => {
      try {
        dispatch(showLoadingOverlay())
        await updateCustomer.mutateAsync({ id, data: { isDeleted: false } })
      } catch (error) {
        console.log(error)
      } finally {
        dispatch(hideLoadingOverlay())
      }
    },
    [dispatch, updateCustomer]
  )

  const defaultColumns = getCustomerTableColumns({
    handleDelete,
    handleActivate,
  })

  if (isLoading) {
    return <LoadingSection />
  }

  return (
    <>
      <CustomerDetailModal modalDetail={modalDetail} setModalDetail={setModalDetail} />
      <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
        <CustomerListHeader handleAddCustomer={handleAddCustomer} />
        <Box p={5} overflow="scroll">
          <FullTable
            data={customersResponse?.data ? customersResponse.data : []}
            rowCount={customersResponse?.count}
            columnDef={defaultColumns}
            state={storedTableState}
            setState={setStoredTableState}
            handleRowClick={handleOnRowClick}
          />
        </Box>
      </Box>
    </>
  )
}

export default CustomerListPage
